/**
 * Hotel Service
 * Handles CRUD operations for hotels linked to packages
 */

import { supabase } from './supabaseService';

export interface Hotel {
  id: string;
  packageId: string;
  name: string;
  description?: string;
  contactEmail?: string;
  contactPhone?: string;
  contactWhatsapp?: string;
  website?: string;
  address?: string;
  location?: any;
  images?: string[];
  amenities?: string[];
  roomTypes?: any[];
  priceRange?: string;
  rating?: number;
  reviewCount?: number;
  active: boolean;
  sortOrder?: number;
  createdAt: string;
  updatedAt: string;
}

class HotelService {
  /**
   * Get all hotels for a package
   */
  async getHotelsByPackage(packageId: string): Promise<Hotel[]> {
    try {
      const { data, error } = await supabase
        .from('hotels')
        .select('*')
        .eq('package_id', packageId)
        .eq('active', true)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Error fetching hotels:', error);
        return [];
      }

      return (data || []).map(this.mapDbToHotel);
    } catch (error) {
      console.error('Error in getHotelsByPackage:', error);
      return [];
    }
  }

  /**
   * Get hotel by ID
   */
  async getHotelById(id: string): Promise<Hotel | null> {
    try {
      const { data, error } = await supabase
        .from('hotels')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching hotel:', error);
        return null;
      }

      return data ? this.mapDbToHotel(data) : null;
    } catch (error) {
      console.error('Error in getHotelById:', error);
      return null;
    }
  }

  /**
   * Create a new hotel
   */
  async createHotel(hotelData: Omit<Hotel, 'id' | 'createdAt' | 'updatedAt'>): Promise<Hotel> {
    try {
      const dbHotel = this.mapHotelToDb(hotelData);
      
      const { data, error } = await supabase
        .from('hotels')
        .insert([dbHotel])
        .select()
        .single();

      if (error) {
        console.error('Error creating hotel:', error);
        throw error;
      }

      return this.mapDbToHotel(data);
    } catch (error) {
      console.error('Error in createHotel:', error);
      throw error;
    }
  }

  /**
   * Update a hotel
   */
  async updateHotel(id: string, updates: Partial<Omit<Hotel, 'id' | 'createdAt'>>): Promise<Hotel | null> {
    try {
      const dbUpdates = this.mapHotelToDb(updates as Hotel, true);
      dbUpdates.updated_at = new Date().toISOString();

      const { data, error } = await supabase
        .from('hotels')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating hotel:', error);
        return null;
      }

      return data ? this.mapDbToHotel(data) : null;
    } catch (error) {
      console.error('Error in updateHotel:', error);
      return null;
    }
  }

  /**
   * Delete a hotel
   */
  async deleteHotel(id: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('hotels')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting hotel:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in deleteHotel:', error);
      return false;
    }
  }

  /**
   * Map database format to Hotel interface
   */
  private mapDbToHotel(dbHotel: any): Hotel {
    return {
      id: dbHotel.id,
      packageId: dbHotel.package_id,
      name: dbHotel.name,
      description: dbHotel.description,
      contactEmail: dbHotel.contact_email,
      contactPhone: dbHotel.contact_phone,
      contactWhatsapp: dbHotel.contact_whatsapp,
      website: dbHotel.website,
      address: dbHotel.address,
      location: dbHotel.location,
      images: dbHotel.images || [],
      amenities: dbHotel.amenities || [],
      roomTypes: dbHotel.room_types || [],
      priceRange: dbHotel.price_range,
      rating: dbHotel.rating,
      reviewCount: dbHotel.review_count,
      active: dbHotel.active,
      sortOrder: dbHotel.sort_order,
      createdAt: dbHotel.created_at,
      updatedAt: dbHotel.updated_at
    };
  }

  /**
   * Map Hotel interface to database format
   */
  private mapHotelToDb(hotelData: Partial<Hotel>, isUpdate: boolean = false): any {
    const dbHotel: any = {};

    if (hotelData.packageId !== undefined) dbHotel.package_id = hotelData.packageId;
    if (hotelData.name !== undefined) dbHotel.name = hotelData.name;
    if (hotelData.description !== undefined) dbHotel.description = hotelData.description;
    if (hotelData.contactEmail !== undefined) dbHotel.contact_email = hotelData.contactEmail;
    if (hotelData.contactPhone !== undefined) dbHotel.contact_phone = hotelData.contactPhone;
    if (hotelData.contactWhatsapp !== undefined) dbHotel.contact_whatsapp = hotelData.contactWhatsapp;
    if (hotelData.website !== undefined) dbHotel.website = hotelData.website;
    if (hotelData.address !== undefined) dbHotel.address = hotelData.address;
    if (hotelData.location !== undefined) dbHotel.location = hotelData.location;
    if (hotelData.images !== undefined) dbHotel.images = hotelData.images;
    if (hotelData.amenities !== undefined) dbHotel.amenities = hotelData.amenities;
    if (hotelData.roomTypes !== undefined) dbHotel.room_types = hotelData.roomTypes;
    if (hotelData.priceRange !== undefined) dbHotel.price_range = hotelData.priceRange;
    if (hotelData.rating !== undefined) dbHotel.rating = hotelData.rating;
    if (hotelData.reviewCount !== undefined) dbHotel.review_count = hotelData.reviewCount;
    if (hotelData.active !== undefined) dbHotel.active = hotelData.active;
    if (hotelData.sortOrder !== undefined) dbHotel.sort_order = hotelData.sortOrder;

    return dbHotel;
  }
}

export const hotelService = new HotelService();

